import { useContext } from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import { t } from 'ttag'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { lists } from '@h2/components/mixins'
import { features } from '@h2/components/media'
import { PB97T, BLACK } from '@h2/components/colors'
import { Skeleton } from '@h2/components/loading/skeleton'
import { Flex } from '@h2/components/layout/flex'
import { Box } from '@h2/components/layout/box'
import { Heading } from '@h2/components/typography/heading'
import { Text } from '@h2/components/typography/text'
import { Icon as IconSvg } from '@h2/components/display/icon'

import { imageMap } from './popular'

import { Link } from '../../../link'
import { Context } from '../../context'

const Recent = styled.ul`
  ${lists};

  > li {
    margin-bottom: 15px;

    &:last-child {
      margin-bottom: 0;
    }

    &.loading {
      display: flex;
      align-items: center;

      > span + span {
        margin-left: 5px;
      }

      > span:first-child {
        flex: 1 0 auto;
      }
    }
  }
`
const IconDefault = styled.span`
  border-radius: 50%;
  background-color: ${PB97T};
  border: 1px solid ${PB97T};
  width: 24px;
  height: 24px;
`
const RecentLink = styled(Link)`
  display: inline-flex;
  align-items: center;

  > span[role='img'] {
    border-radius: 50%;
    background-color: ${PB97T};
    border: 1px solid ${PB97T};
    padding: 2px;
  }

  > span:last-child {
    margin-left: 5px;
  }

  &:hover {
    text-decoration: none;
  }
`
const Popular = styled.ul`
  ${lists};

  > li {
    background-color: ${PB97T};
    border: 1px solid ${PB97T};
    border-radius: 4px;

    &:nth-child(n + 2) {
      margin-top: 10px;
    }

    &:hover {
      background-color: white;
    }
  }
`
const PopularLink = styled(Link)`
  display: block;
  padding: 10px;
  color: white;

  > figure {
    display: flex;
    align-items: flex-end;

    figcaption {
      margin-left: 10px;
    }
  }

  &:hover {
    color: ${({ href }) => imageMap[href].color};
    text-decoration: none;

    path[data-currentcolor] {
      opacity: 0.15;
    }
  }
`
const FlexColumn = styled(Flex)`
  display: none;

  @media (${features.minWidth.laptop}) {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    overflow-y: auto;
    height: 100%;
    width: 20%;
    min-width: 345px;
    background-color: white;
    padding: 20px 30px;

    > div:first-child {
      margin: 0 0 20px 0;
    }
  }
`
const RecentActivitiesPopular = ({ icons }) => {
  const { isLoggedIn, userMenu, isMenuFetching, useReactRouter } = useContext(Context)

  return (
    <FlexColumn>
      {isLoggedIn && (
        <Box>
          <Heading level="4" mb="10px">
            {t`Recent Activities`}
          </Heading>
          <nav>
            <Recent>
              {isMenuFetching
                ? Array(4)
                    .fill()
                    .map((item, idx) => (
                      <li key={idx} className="loading">
                        <Skeleton circle isAnimated={false} radius="12px" />
                        <Skeleton />
                      </li>
                    ))
                : userMenu?.recentFeatures.map(({ title, url, useRouter, sectionId }) => {
                    const Icon = icons?.[sectionId]

                    return (
                      <li key={url}>
                        <RecentLink ssr={!useRouter || !useReactRouter} href={url}>
                          {Icon ? <Icon size="tiny" /> : <IconDefault />}
                          <Text as="span" size="medium" color={BLACK}>
                            {title}
                          </Text>
                        </RecentLink>
                      </li>
                    )
                  })}
              {userMenu?.recentFeatures.length === 0 && (
                <Flex direction="column" alignItems="center" padding="25px">
                  <FontAwesomeIcon icon="history" size="5x" color={PB97T} />
                  <Heading level="5" mb="8px" mt="10px" variant="font">
                    {t`You have no recent activity`}
                  </Heading>
                  <Text size="small">
                    {t`As you complete activities in the CareerExplorer they will appear here for quick and easy access.`}
                  </Text>
                </Flex>
              )}
            </Recent>
          </nav>
        </Box>
      )}
      <Box>
        <Heading level="4" mb="10px">
          {t`Popular Features`}
        </Heading>
        <Popular>
          {isMenuFetching
            ? Array(3)
                .fill()
                .map((item, idx) => (
                  <li key={idx}>
                    <Skeleton display="block" height="100px" />
                  </li>
                ))
            : userMenu?.topFeatures.map(({ title, description, url, useRouter }) => (
                <li key={url}>
                  <PopularLink ssr={!useRouter || !useReactRouter} href={url}>
                    <figure>
                      <IconSvg
                        width="auto"
                        height="70px"
                        dangerouslySetGlyph={imageMap[url].svg}
                      />
                      <figcaption>
                        <Heading level="5" mb="5px">
                          {title}
                        </Heading>
                        {description && <Text size="small">{description}</Text>}
                      </figcaption>
                    </figure>
                  </PopularLink>
                </li>
              ))}
        </Popular>
      </Box>
    </FlexColumn>
  )
}

RecentActivitiesPopular.propTypes = {
  icons: PropTypes.object
}

export { RecentActivitiesPopular }
